import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ANDROID_PLAY_STORE_URL, IOS_APP_STORE_URL } from '../../utils/openInAppLinks';
import { requestApiJson } from '../../api/client';

// macOS版サイドバーのストアボタンデザインを踏襲（縦積みボタン、アイコン＋2段ラベル）。
// Mac は AppDownloadBanner と同じ配信経路（/api/mac-release/latest）を共有し、
// 公開中（latest.json が success）のときだけ表示する。
const STORE_LINKS = [
  {
    key: 'ios',
    label: 'for iOS',
    sublabel: 'AppStoreへ',
    iconClass: 'fa-brands fa-apple',
    href: IOS_APP_STORE_URL,
    tintClass: 'sidebar-store-button--ios',
  },
  {
    key: 'android',
    label: 'for Android',
    sublabel: 'GooglePlayへ',
    iconClass: 'fa-brands fa-google-play',
    href: ANDROID_PLAY_STORE_URL,
    tintClass: 'sidebar-store-button--android',
  },
  {
    key: 'mac',
    label: 'for Mac',
    sublabel: 'ダウンロード',
    iconClass: 'fa-solid fa-desktop',
    href: '/api/mac-release/latest',
    tintClass: 'sidebar-store-button--mac',
  },
];

const MAC_RELEASE_STATUS_PATH = '/api/mac-release/latest.json';

// API オリジンを解決して /api パスを絶対URL化する（AppDownloadBanner と同一ロジック）。
const buildApiPath = (path) => {
  const apiOrigin = String(process.env.REACT_APP_API_URL || '')
    .replace(/\/api\/?$/i, '')
    .replace(/\/+$/, '');
  return apiOrigin ? `${apiOrigin}${path}` : path;
};

function SidebarAppStoreButtons() {
  const [macReleaseAvailable, setMacReleaseAvailable] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let cancelled = false;
    (async () => {
      try {
        const { data, response } = await requestApiJson(MAC_RELEASE_STATUS_PATH, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: controller?.signal,
        });
        if (cancelled) return;
        setMacReleaseAvailable(response.ok && data?.success === true);
      } catch (releaseError) {
        if (!cancelled && releaseError?.name !== 'AbortError') setMacReleaseAvailable(false);
      }
    })();
    return () => {
      cancelled = true;
      controller?.abort();
    };
  }, []);

  const links = STORE_LINKS.filter((link) => link.key !== 'mac' || macReleaseAvailable);

  return (
    <div className="sidebar-store-buttons">
      {links.map((link) => (
        <a
          key={link.key}
          href={link.key === 'mac' ? buildApiPath(link.href) : link.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`sidebar-store-button ${link.tintClass}`}
        >
          <i className={`${link.iconClass} sidebar-store-button__icon`} aria-hidden="true" />
          <span className="sidebar-store-button__label">
            <span className="sidebar-store-button__primary">{link.label}</span>
            <span className="sidebar-store-button__secondary">{link.sublabel}</span>
          </span>
        </a>
      ))}
    </div>
  );
}

function SectionLabel({ iconClass, text }) {
  return (
    <div className="sidebar-label">
      <i className={iconClass} aria-hidden="true" />
      <span>{text}</span>
    </div>
  );
}

function SidebarLink({ to, label, isActivePath, badge = null }) {
  return (
    <li className="sidebar-item">
      <Link to={to} className={isActivePath(to, { exact: true }) ? 'is-active' : ''}>
        <span>{label}</span>
        {badge}
      </Link>
    </li>
  );
}

// クリックしてもページ遷移せず、配下のサブメニューを開閉するだけの折りたたみ見出し。
function SidebarSubgroup({ groupKey, label, iconClass, isOpen, onToggle, children }) {
  const panelId = `sidebar-subgroup-${groupKey}`;
  return (
    <li className="sidebar-item sidebar-subgroup">
      <button
        type="button"
        className={`sidebar-subgroup__toggle${isOpen ? ' is-open' : ''}`}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span className="sidebar-item__content">
          <i className={`${iconClass} sidebar-item__icon`} aria-hidden="true" />
          <span>{label}</span>
        </span>
        <i className="fa-solid fa-chevron-down sidebar-subgroup__chevron" aria-hidden="true" />
      </button>
      {isOpen && (
        <ul id={panelId} className="sidebar-subgroup__list">
          {children}
        </ul>
      )}
    </li>
  );
}

function TaskSidebarSection({
  isTaskOnlyAccount,
  taskLinkedEventCount,
  taskProjects,
  taskProjectsLoading,
  taskProjectsError,
  currentPath,
  isActivePath,
}) {
  return (
    <>
      <SectionLabel iconClass="fa-solid fa-calendar-days" text="参加イベント" />
      <ul>
        <li className="sidebar-item">
          <Link
            to="/admin/workspaces/task-management/events"
            className={isActivePath('/admin/workspaces/task-management/events', { exact: true }) ? 'is-active' : ''}
          >
            <span>イベント</span>
            <span className="sidebar-task-project-count">{taskLinkedEventCount}</span>
          </Link>
        </li>
      </ul>
      <SectionLabel iconClass="fa-solid fa-folder-open" text="参加プロジェクト" />
      <ul className="sidebar-task-project-list">
        {taskProjectsLoading ? (
          <li className="sidebar-item">
            <span className="sidebar-task-project-state">読み込み中...</span>
          </li>
        ) : null}
        {!taskProjectsLoading && taskProjectsError ? (
          <li className="sidebar-item">
            <span className="sidebar-task-project-state is-error">取得に失敗しました</span>
          </li>
        ) : null}
        {!taskProjectsLoading && !taskProjectsError && taskProjects.length === 0 ? (
          <li className="sidebar-item">
            <span className="sidebar-task-project-state">参加プロジェクトはありません</span>
          </li>
        ) : null}
        {!taskProjectsLoading && !taskProjectsError
          ? taskProjects.slice(0, 20).map((project) => {
              const projectPath = `/admin/workspaces/task-management/projects/${encodeURIComponent(project.id)}`;
              const isProjectActive = currentPath === projectPath;
              return (
                <li key={project.id} className="sidebar-item">
                  <Link to={projectPath} className={isProjectActive ? 'is-active' : ''}>
                    <span className="sidebar-task-project-content">
                      <span className="sidebar-task-project-name">{project.name || '名称未設定'}</span>
                      {project.eventName ? (
                        <span className="sidebar-task-project-meta">{project.eventName}</span>
                      ) : null}
                    </span>
                    <span className="sidebar-task-project-count">{Number(project.openTaskCount || 0)}</span>
                  </Link>
                </li>
              );
            })
          : null}
      </ul>
      {isTaskOnlyAccount ? (
        <>
          <SectionLabel iconClass="fa-solid fa-gear" text="個人設定" />
          <ul>
            <li className="sidebar-item">
              <Link
                to="/admin/settings/profile"
                className={isActivePath('/admin/settings/profile', { exact: true }) ? 'is-active' : ''}
              >
                <span>プロフィール編集</span>
              </Link>
            </li>
            <li className="sidebar-item">
              <Link
                to="/admin/settings/notifications"
                className={isActivePath('/admin/settings/notifications', { exact: true }) ? 'is-active' : ''}
              >
                <span>通知設定</span>
              </Link>
            </li>
            <li className="sidebar-item">
              <Link
                to="/admin/settings/work-availability"
                className={isActivePath('/admin/settings/work-availability', { exact: true }) ? 'is-active' : ''}
              >
                <span>稼働状況設定</span>
              </Link>
            </li>
            <li className="sidebar-item">
              <Link
                to="/admin/settings/change-password"
                className={isActivePath('/admin/settings/change-password', { exact: true }) ? 'is-active' : ''}
              >
                <span>パスワード変更</span>
              </Link>
            </li>
          </ul>
        </>
      ) : null}
    </>
  );
}

function SystemSettingsSection({
  can,
  canManageAccounts,
  canManageMacRelease,
  canManageMobileRelease,
  canManagePolicies,
  canViewActionLogs,
  canViewAnnouncements,
  canViewBugs,
  canViewOperationalEmails,
  canManageServiceAiSettings,
  canManageContractClauseSettings,
  canManagePlayguides,
  isSuperAdmin,
  pendingApprovalCount,
  isActivePath,
}) {
  // システム管理系はサブグループにまとめ、折りたたみで表示（権限で0件のグループは非表示）。
  const systemGroups = [
    {
      key: 'operations',
      label: '運営',
      iconClass: 'fa-solid fa-bullhorn',
      items: [
        canViewAnnouncements && {
          to: '/admin/workspaces/system/announcements',
          label: 'お知らせ管理',
          badge:
            isSuperAdmin && pendingApprovalCount > 0 ? (
              <span className="sidebar-count-badge">
                {pendingApprovalCount > 99 ? '99+' : pendingApprovalCount}
              </span>
            ) : null,
        },
        canViewOperationalEmails && {
          to: '/admin/workspaces/system/operational-emails',
          label: 'メール配信',
        },
        canManagePolicies && {
          to: '/admin/workspaces/system/policies',
          label: '規約管理',
        },
        canManageServiceAiSettings && {
          to: '/admin/workspaces/system/service-ai-settings',
          label: 'AI設定',
        },
        canManageContractClauseSettings && {
          to: '/admin/workspaces/system/contract-clause-settings',
          label: '条項設定',
        },
        canManagePlayguides && {
          to: '/admin/workspaces/system/playguide-connections',
          label: '外部チケット連携',
        },
      ].filter(Boolean),
    },
    {
      key: 'platform',
      label: 'アプリ管理',
      iconClass: 'fa-solid fa-mobile-screen-button',
      items: [
        canManageMobileRelease && {
          to: '/admin/workspaces/system/mobile-release',
          label: 'モバイル更新管理',
        },
        canManageMacRelease && {
          to: '/admin/workspaces/system/mac-release',
          label: 'Mac更新管理',
        },
      ].filter(Boolean),
    },
    {
      key: 'audit',
      label: '監査・権限',
      iconClass: 'fa-solid fa-shield-halved',
      items: [
        canViewActionLogs && {
          to: '/admin/workspaces/system/action-logs',
          label: 'アクションログ',
        },
        canManageAccounts && {
          to: '/admin/workspaces/system/account-management',
          label: 'アカウント管理',
        },
      ].filter(Boolean),
    },
    {
      key: 'others',
      label: 'その他',
      iconClass: 'fa-solid fa-circle-info',
      items: [
        can('SYSTEM_VIEW_MANUALS') && {
          to: '/admin/workspaces/system/manuals',
          label: 'マニュアル',
        },
        canViewBugs && {
          to: '/admin/workspaces/system/bugs',
          label: 'バグ管理',
          badge: <span className="sidebar-count-badge sidebar-count-badge--beta">BETA</span>,
        },
      ].filter(Boolean),
    },
  ].filter((group) => group.items.length > 0);

  // 現在地を含むグループは初期状態で開いておく。
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    systemGroups.forEach((group) => {
      initial[group.key] = group.items.some((item) => isActivePath(item.to, { exact: true }));
    });
    return initial;
  });
  const toggleGroup = (key) =>
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <>
      <SectionLabel iconClass="fa-solid fa-user-gear" text="個人設定" />
      <ul>
        <SidebarLink to="/admin/settings/profile" label="プロフィール編集" isActivePath={isActivePath} />
        <SidebarLink to="/admin/settings/notifications" label="通知設定" isActivePath={isActivePath} />
        <SidebarLink to="/admin/settings/work-availability" label="稼働状況設定" isActivePath={isActivePath} />
      </ul>
      {systemGroups.length > 0 && (
        <>
          <SectionLabel iconClass="fa-solid fa-gear" text="システム管理" />
          <ul>
            {systemGroups.map((group) => (
              <SidebarSubgroup
                key={group.key}
                groupKey={group.key}
                label={group.label}
                iconClass={group.iconClass}
                isOpen={Boolean(openGroups[group.key])}
                onToggle={() => toggleGroup(group.key)}
              >
                {group.items.map((item) => (
                  <SidebarLink
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    isActivePath={isActivePath}
                    badge={item.badge}
                  />
                ))}
              </SidebarSubgroup>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

function SidebarBottom({ onLogout }) {
  return (
    <div className="sidebar-bottom">
      <ul className="sidebar-bottom__actions">
        <li className="sidebar-item logout" onClick={onLogout}>
          <span className="sidebar-item__content">
            <i className="fa-solid fa-right-from-bracket sidebar-item__icon logout-icon" aria-hidden="true" />
            <span>ログアウト</span>
          </span>
        </li>
      </ul>
      <SidebarAppStoreButtons />
      <div className="sidebar-legal">
        <Link to="/terms" target="_blank" rel="noopener noreferrer" className="sidebar-legal__link">
          利用規約
        </Link>
        <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="sidebar-legal__link">
          プライバシーポリシー
        </Link>
      </div>
    </div>
  );
}

export { SectionLabel, SidebarBottom, SystemSettingsSection, TaskSidebarSection };
